import { useState } from "react";
import api from "../services/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Search, Package, Truck, MapPin, Clock, CheckCircle2, Loader2,
  Building2, Zap, RotateCcw, Phone, User, ShieldCheck, Download,
} from "lucide-react";
import Logo from "../components/Logo";
import { useNavigate } from "react-router-dom";

const FORWARD_STAGES = [
  { key: "pending", label: "Order Placed", icon: Package },
  { key: "pickup_assigned", label: "Pickup Assigned", icon: User },
  { key: "picked_up", label: "Picked Up", icon: CheckCircle2 },
  { key: "packing", label: "Packing", icon: Package },
  { key: "at_origin_hub", label: "Origin Hub", icon: Building2 },
  { key: "in_linehaul", label: "Line-Haul", icon: Truck },
  { key: "at_dest_hub", label: "Dest Hub", icon: Building2 },
  { key: "out_for_delivery", label: "Out for Delivery", icon: Truck },
  { key: "delivered", label: "Delivered", icon: CheckCircle2 },
];

const REVERSE_STAGES = [
  { key: "return_requested", label: "Return Requested", icon: RotateCcw },
  { key: "return_pickup_assigned", label: "Return Pickup", icon: User },
  { key: "return_picked_up", label: "Return Collected", icon: CheckCircle2 },
  { key: "return_at_origin_hub", label: "Return Hub", icon: Building2 },
  { key: "return_in_linehaul", label: "Return Transit", icon: Truck },
  { key: "return_at_dest_hub", label: "Return Dest Hub", icon: Building2 },
  { key: "return_delivered", label: "Returned", icon: CheckCircle2 },
];

export default function B2BTracking() {
  const navigate = useNavigate();
  const [trackingId, setTrackingId] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!trackingId.trim()) return;
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.get(`/orders/public-track/${trackingId.trim()}`);
      if (data.order_type === "b2b_express" || data.order_type === "b2b_return") {
        setOrder(data);
      } else {
        setError("This is not a B2B order. Use regular tracking for B2C orders.");
      }
    } catch {
      setError("Order not found. Please check the tracking ID.");
    }
    setLoading(false);
  };

  const isReturn = order?.order_type === "b2b_return";
  const stages = isReturn ? REVERSE_STAGES : FORWARD_STAGES;
  const currentIdx = order ? stages.findIndex(s => s.key === order.status) : -1;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="b2b-tracking-page">
      {/* Header */}
      <div className="bg-[#0B132B] text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <div className="cursor-pointer" onClick={() => navigate("/b2b")}>
              <Logo size="md" textColor="text-white" />
            </div>
            <span className="inline-flex items-center gap-1.5 bg-[#FF6B00]/20 text-[#FF6B00] px-3 py-1 rounded-full text-xs font-bold">
              <Zap className="h-3 w-3" /> B2B Shipment Tracking
            </span>
          </div>
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Enter B2B tracking ID (e.g., B2B-53DA36F0 or RTN-33EB23F7)"
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value.toUpperCase())}
                className="h-14 pl-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-slate-400 text-base"
                data-testid="b2b-tracking-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-14 px-8 text-base font-semibold" data-testid="b2b-tracking-search">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Track"}
            </Button>
          </form>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-xl text-sm" data-testid="tracking-error">{error}</div>
        )}

        {order && (
          <div className="space-y-6 animate-fadeInUp">
            {/* Order Summary Card */}
            <div className={`rounded-2xl border-2 p-6 ${isReturn ? "border-red-200 bg-red-50/30" : "border-[#FF6B00]/30 bg-orange-50/30"}`} data-testid="order-summary">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-mono text-xl font-bold ${isReturn ? "text-red-600" : "text-[#FF6B00]"}`}>{order.tracking_id}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${isReturn ? "bg-red-100 text-red-700 border-red-200" : "bg-orange-100 text-[#FF6B00] border-orange-200"}`}>
                      {isReturn ? "RETURN" : "B2B EXPRESS"}
                    </span>
                  </div>
                  {isReturn && order.original_tracking_id && (
                    <p className="text-xs text-slate-500">Original: <span className="font-mono font-semibold">{order.original_tracking_id}</span></p>
                  )}
                  <p className="text-sm text-slate-600 mt-1">{order.package_description}</p>
                </div>
                <div className="text-right">
                  {order.sla_days && (
                    <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 rounded-lg text-xs font-bold">
                      <ShieldCheck className="h-3.5 w-3.5" /> {order.sla_days}-Day SLA
                    </span>
                  )}
                  <p className="text-lg font-bold text-slate-900 mt-1">INR {order.price?.total?.toLocaleString()}</p>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/invoice/${order.tracking_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#FF6B00] hover:text-orange-700 hover:underline"
                    data-testid="download-invoice-btn"
                  >
                    <Download className="h-3.5 w-3.5" /> Download Invoice
                  </a>
                </div>
              </div>
            </div>

            {/* 6-Stage Visual Pipeline */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6" data-testid="tracking-pipeline">
              <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900 mb-6">Shipment Pipeline</h2>
              <div className="relative">
                {stages.map((stage, i) => {
                  const isDone = i <= currentIdx && currentIdx >= 0;
                  const isCurrent = stage.key === order.status;
                  const Icon = stage.icon;
                  const timestamp = order[`${stage.key}_at`] || (stage.key === "pending" ? order.created_at : null);

                  return (
                    <div key={stage.key} className="flex items-start gap-4 relative" data-testid={`pipeline-stage-${stage.key}`}>
                      {/* Connector line */}
                      {i < stages.length - 1 && (
                        <div className={`absolute left-5 top-10 w-0.5 h-12 ${isDone && i < currentIdx ? (isReturn ? "bg-red-400" : "bg-emerald-400") : "bg-slate-200"}`} />
                      )}
                      {/* Circle */}
                      <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all ${
                        isCurrent
                          ? isReturn ? "bg-red-500 text-white ring-4 ring-red-100" : "bg-[#FF6B00] text-white ring-4 ring-orange-100"
                          : isDone
                            ? isReturn ? "bg-red-400 text-white" : "bg-emerald-500 text-white"
                            : "bg-slate-100 text-slate-400"
                      }`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      {/* Text */}
                      <div className="pb-10 pt-1.5 flex-1">
                        <p className={`text-sm font-semibold ${isCurrent ? "text-slate-900" : isDone ? "text-slate-700" : "text-slate-400"}`}>{stage.label}</p>
                        {timestamp && (
                          <p className="text-[10px] text-slate-400 mt-0.5">{new Date(timestamp).toLocaleString()}</p>
                        )}
                        {isCurrent && (
                          <span className={`inline-flex items-center gap-1 mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${isReturn ? "bg-red-100 text-red-700" : "bg-orange-100 text-[#FF6B00]"}`}>
                            <Clock className="h-2.5 w-2.5" /> Current Stage
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Route & Agent Info */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Route Details</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Pickup</p>
                      <p className="text-sm text-slate-700">{order.pickup_address}</p>
                      <p className="text-xs text-slate-400">{order.pickup_city}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Delivery</p>
                      <p className="text-sm text-slate-700">{order.delivery_address}</p>
                      <p className="text-xs text-slate-400">{order.delivery_city}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 pt-1">
                    <span>Weight: <strong>{order.package_weight}kg</strong></span>
                    {order.price?.item_count > 1 && <span>Items: <strong>{order.price.item_count}</strong></span>}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Agent Details</h3>
                <div className="space-y-3">
                  {order.pickup_agent_name && (
                    <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <User className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">{order.pickup_agent_name}</p>
                        {order.pickup_agent_phone && <p className="text-[10px] text-slate-500 flex items-center gap-1"><Phone className="h-2.5 w-2.5" />{order.pickup_agent_phone}</p>}
                        <p className="text-[9px] text-blue-600 font-semibold">Pickup Agent {order.pickup_vehicle_type ? `(${order.pickup_vehicle_type})` : ""}</p>
                      </div>
                    </div>
                  )}
                  {order.delivery_agent_name && (
                    <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
                      <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <Truck className="h-4 w-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">{order.delivery_agent_name}</p>
                        {order.delivery_agent_phone && <p className="text-[10px] text-slate-500 flex items-center gap-1"><Phone className="h-2.5 w-2.5" />{order.delivery_agent_phone}</p>}
                        <p className="text-[9px] text-emerald-600 font-semibold">Delivery Agent {order.delivery_vehicle_type ? `(${order.delivery_vehicle_type})` : ""}</p>
                      </div>
                    </div>
                  )}
                  {!order.pickup_agent_name && !order.delivery_agent_name && (
                    <p className="text-sm text-slate-400 text-center py-4">Agent not assigned yet</p>
                  )}
                  {isReturn && order.return_reason && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                      <p className="text-[10px] font-bold text-red-600 uppercase">Return Reason</p>
                      <p className="text-sm text-red-800 font-semibold">{order.return_reason}</p>
                      {order.return_notes && <p className="text-xs text-red-600 mt-1">{order.return_notes}</p>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {!order && !error && !loading && (
          <div className="text-center py-16">
            <Package className="h-16 w-16 text-slate-200 mx-auto mb-4" />
            <h2 className="font-['Outfit'] text-xl font-bold text-slate-700">Track Your B2B Shipment</h2>
            <p className="text-sm text-slate-400 mt-2">Enter your B2B or Return tracking ID above</p>
          </div>
        )}
      </div>
    </div>
  );
}
